import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";

import DatagridTooltipProvider from "./datagridTooltipProvider.js";
import { useMemo, useRef, useState } from "react";

import { Delete, Edit, List, Refresh } from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/FilterAltOff";
import ClearSortingIcon from "@mui/icons-material/MobiledataOff";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import { lighten } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";

import {
  mrtGetActionColumnStyling,
  mrtGetActionIconSx,
  mrtGetDateColumnSizing,
  mrtGetTableWrapperSx,
} from "../../utils/helpers";
import MRTDateTimeCell from "./mrtDateTimeCell";

const getTextCellProps = theme => {
  return {
    sx: {
      whiteSpace: "normal",
      wordWrap: "break-word",
      borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
      padding: "4px 3px",
      verticalAlign: "top",
    },
  };
};

// Display-only: strip any directory / preamble so the column shows just the
// file name (e.g. "kb_01.../FAQ.md" -> "FAQ.md"). The stored value is unchanged.
const getSourceFileDisplayName = path => {
  if (!path) {
    return "";
  }

  const normalized = String(path).replace(/\\/g, "/");
  const parts = normalized.split("/");

  return parts[parts.length - 1] || normalized;
};

const renderTextCell = cell => {
  return (
    <Typography
      variant="body2"
      sx={{
        display: "-webkit-box",
        WebkitLineClamp: 4,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "normal",
        wordBreak: "break-word",
        fontSize: "0.74rem",
        lineHeight: 1.1,
      }}
    >
      {cell.getValue() || ""}
    </Typography>
  );
};

const renderSourceFileCell = cell => {
  return (
    <Typography
      variant="body2"
      sx={{
        display: "-webkit-box",
        WebkitLineClamp: 4,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "normal",
        wordBreak: "break-word",
        fontSize: "0.74rem",
        lineHeight: 1.1,
      }}
    >
      {getSourceFileDisplayName(cell.getValue())}
    </Typography>
  );
};

const calculateColumns = theme => {
  return [
    {
      accessorKey: "createdAt",
      header: "Created",
      ...mrtGetDateColumnSizing(),
      enableColumnFilterModes: false,
      enableSorting: true,
      enableColumnFilter: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
          padding: "4px 3px",
          verticalAlign: "top",
        },
      },
      Cell: ({ cell }) => <MRTDateTimeCell cell={cell} compact />,
    },
    {
      accessorKey: "topic",
      header: "Topic or Question",
      size: 220,
      minSize: 160,
      maxSize: 320,
      enableColumnFilterModes: false,
      enableSorting: false,
      enableColumnFilter: true,
      filterVariant: "text",
      grow: false,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => renderTextCell(cell),
    },
    {
      accessorKey: "clarification",
      header: "Clarification",
      size: 700,
      minSize: 300,
      maxSize: 900,
      enableColumnFilterModes: false,
      enableSorting: false,
      enableColumnFilter: true,
      filterVariant: "text",
      grow: true,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => renderTextCell(cell),
    },
    {
      accessorKey: "sourceFile",
      header: "Source File",
      size: 220,
      minSize: 160,
      maxSize: 300,
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: false,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => renderSourceFileCell(cell),
    },
    {
      accessorKey: "lineNumber",
      header: "Line",
      size: 70,
      maxSize: 90,
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
          padding: "4px 3px",
          verticalAlign: "top",
        },
      },
      Cell: ({ cell }) => (
        <Typography variant="body2" sx={{ fontSize: "0.74rem", lineHeight: 1.1 }}>
          {cell.getValue() || ""}
        </Typography>
      ),
    },
  ];
};

const getInitialState = () => {
  return {
    density: "compact",
    columnOrder: [
      "mrt-row-actions",
      "createdAt",
      "topic",
      "clarification",
      "sourceFile",
      "lineNumber",
    ],
    columnVisibility: {
      createdAt: true,
      topic: true,
      clarification: true,
      sourceFile: false,
      lineNumber: false,
    },
    pagination: {
      pageIndex: 0,
      pageSize: 20,
    },
  };
};

export const BotpressMemberVerifiedAnswersTable = props => {
  const { data = [], onRefreshTable = null, onEditEntry = null, onDeleteEntry = null } = props;

  const tableData = Array.isArray(data) ? data : [];

  const theme = useTheme();
  const mobileLayout = useMediaQuery(theme.breakpoints.down("sm"));
  const tableContainerRef = useRef(null);

  const columns = useMemo(() => calculateColumns(theme), [theme]);
  const initialState = useMemo(() => getInitialState(), []);

  const [isRefreshIconSpinning, setIsRefreshIconSpinning] = useState(false);

  const [sorting, setSorting] = useState([]);

  const [columnFilters, setColumnFilters] = useState([]);
  const [columnOrder, setColumnOrder] = useState(initialState.columnOrder);
  const [columnVisibility, setColumnVisibility] = useState(initialState.columnVisibility);

  const table = useMaterialReactTable({
    columns,
    data: tableData,
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableColumnResizing: false,
    enableGrouping: false,
    enableColumnPinning: false,
    enableFacetedValues: true,
    enableRowActions: onEditEntry != null || onDeleteEntry != null,
    enableRowSelection: false,
    enableStickyHeader: true,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: true,
    enableHiding: true,
    enablePagination: true,
    enableTopToolbar: true,
    enableBottomToolbar: true,
    enableGlobalFilter: false,
    initialState,
    positionActionsColumn: "first",
    getRowId: row => row.id,
    onColumnOrderChange: setColumnOrder,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    defaultColumn: {
      minSize: 60,
      maxSize: 220,
      size: 100,
      grow: false,
    },
    state: {
      columnFilters,
      columnOrder,
      columnVisibility,
      sorting,
    },
    paginationDisplayMode: "pages",
    enableFirstPageButton: false,
    enableLastPageButton: false,
    muiPaginationProps: {
      color: "primary",
      shape: "rounded",
      showRowsPerPage: true,
      variant: "outlined",
    },
    layoutMode: "grid",
    muiTopToolbarProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.secondary.main,
        flexShrink: 0,
        minHeight: "48px",
        "& .MuiIconButton-root": {
          marginLeft: theme.spacing(1),
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
        "& .MuiInputAdornment-root .MuiIconButton-root": {
          border: "none !important",
          backgroundColor: "transparent !important",
          marginLeft: "0px !important",
          color: `${theme.palette.secondary.main} !important`,
          padding: "4px !important",
          borderRadius: "50%",
        },
        "& .MuiInputAdornment-root .MuiIconButton-root:hover": {
          border: "none !important",
          backgroundColor: "rgba(0, 0, 0, 0.2) !important",
        },
        "& .MuiInputAdornment-root .MuiIconButton-root:active": {
          border: "none !important",
          backgroundColor: "rgba(0, 0, 0, 0.3) !important",
        },
        "& .MuiIconButton-root-active": {
          marginLeft: theme.spacing(1),
          color: theme.palette.secondary.contrastText,
          backgroundColor: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
      },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.contrastText,
        flexShrink: 0,
        height: "56px",
        minHeight: "56px",
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      },
    },
    muiTableContainerProps: {
      ref: tableContainerRef,
      sx: mobileLayout
        ? {
            flex: 1,
            minHeight: 0,
            maxHeight: "none",
            overflowX: "auto",
            overflowY: "auto",
            backgroundColor: theme.palette.commonground.main,
            color: theme.palette.commonground.main,
            borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
            borderRight: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
          }
        : {
            flex: 1,
            minHeight: 0,
            maxHeight: "none",
            overflowX: "auto",
            overflowY: "auto",
            backgroundColor: theme.palette.commonground.main,
            color: theme.palette.commonground.main,
            border: `solid 0.5px ${theme.palette.divider}`,
          },
    },
    muiTableProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.main,
        tableLayout: "fixed",
        width: "100%",
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: lighten(theme.palette.commonground.contrastText, 0.5),
        color: theme.palette.commonground.contrastText,
        justifyContent: "start",
        fontWeight: "bold",
        fontSize: "0.72rem",
        padding: "3px 3px",
        whiteSpace: "nowrap",
      },
    },
    displayColumnDefOptions: {
      "mrt-row-actions": {
        ...mrtGetActionColumnStyling(),
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            padding: "4px 6px",
          },
        },
      },
    },
    renderTopToolbarCustomActions: () => <Box sx={{ minHeight: "40px" }} />,
    icons: {
      MoreHorizIcon: () => <List color={"secondary"} sx={mrtGetActionIconSx(theme)} />,
    },
    renderRowActionMenuItems: ({ row, closeMenu }) =>
      [
        onEditEntry != null && (
          <MenuItem
            key="edit"
            onClick={() => {
              onEditEntry(row.original);
              closeMenu();
            }}
          >
            <Edit fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
            <ListItemText disableTypography={true}>
              <Typography variant="h5" color="textSecondary">
                Edit Entry
              </Typography>
            </ListItemText>
          </MenuItem>
        ),
        onDeleteEntry != null && (
          <MenuItem
            key="delete"
            onClick={() => {
              onDeleteEntry(row.original);
              closeMenu();
            }}
          >
            <Delete fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
            <ListItemText disableTypography={true}>
              <Typography variant="h5" color="textSecondary">
                Delete Entry
              </Typography>
            </ListItemText>
          </MenuItem>
        ),
      ].filter(Boolean),
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        {sorting.length > 0 && (
          <Tooltip title="Clear All Sorting" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setSorting([]);
              }}
            >
              <ClearSortingIcon />
            </IconButton>
          </Tooltip>
        )}

        {columnFilters.length > 0 && (
          <Tooltip title="Clear All Filters" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setColumnFilters([]);
              }}
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Refresh frequently asked questions" arrow>
          <IconButton
            onClick={() => {
              setIsRefreshIconSpinning(true);

              if (onRefreshTable) {
                onRefreshTable();
              }

              setTimeout(() => {
                setIsRefreshIconSpinning(false);
              }, 800);
            }}
          >
            <Refresh
              sx={{
                transformOrigin: "center",
                animation: isRefreshIconSpinning ? "refreshSpin 0.8s linear 1" : "none",
                "@keyframes refreshSpin": {
                  from: {
                    transform: "rotate(0deg)",
                  },
                  to: {
                    transform: "rotate(360deg)",
                  },
                },
              }}
            />
          </IconButton>
        </Tooltip>

        {/* eslint-disable react/jsx-pascal-case */}
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        {/* eslint-enable react/jsx-pascal-case */}
      </Box>
    ),
    renderEmptyRowsFallback: () => (
      <Typography sx={{ padding: theme.spacing(2) }}>
        No frequently asked question markdown files found.
      </Typography>
    ),
  });

  return (
    <Box sx={mrtGetTableWrapperSx(0, !tableData?.length)}>
      <DatagridTooltipProvider>
        <MaterialReactTable table={table} />
      </DatagridTooltipProvider>
    </Box>
  );
};

export default BotpressMemberVerifiedAnswersTable;
